import { GlobalConfigType, setGlobalConfig, setUserPermissions } from 'actions'
import { API_USER_PERMISSIONS, getUserAccountTypes } from 'config'
import { useCallback, useEffect } from 'react'
import { useQuery } from 'react-query'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { store } from 'reducers'
import Api from 'services/api'
import { useQueryRecord } from 'utils'

import { hasPagePermissions } from './hasPagePermissions'

export const usePermissions = () => {
  const auth = useSelector((state: any) => state.auth)
  const pageParams = useParams() as any
  const pageQueries = useQueryRecord()

  const { data, isLoading, error, refetch } = useQuery(
    'permissions',
    async () => {
      return Api.get(API_USER_PERMISSIONS)
    },
    {
      enabled: auth.isAuthenticated,
      refetchOnWindowFocus: true,
      retryDelay: 45000,
      staleTime: 1000 * 60 * 5,
    },
  )

  useEffect(() => {
    try {
      if (!data) return
      store.dispatch(setUserPermissions(data))
      const pagePerms = (store.getState() as any).globalConfigReducer?.[GlobalConfigType.PagePermissions] || {}
      store.dispatch(
        setGlobalConfig(GlobalConfigType.PagePermissions, {
          ...pagePerms,
          userDenies: data.pagePermissionDenies || [],
        }),
      )
    } catch (e) {}
  }, [data])

  // react-query cache is empty after a hard refresh; auth.role is redux-persisted from the
  // last successful /user/permissions fetch. Prefer live data, fall back so permission-gated
  // UI (e.g. Manage Accounts → Change Password) does not vanish while the query rehydrates.
  const permissionSource = data ?? (auth?.role?.role ? auth.role : undefined)

  const hasPermissionLink = useCallback(
    (permissionLevel: string | Array<string>) => {
      if (permissionLevel.length == 0) return true
      if (permissionSource === undefined) return undefined

      let matched = false
      if (Array.isArray(permissionLevel)) {
        // A user can hold several account types, and any one of them is enough to pass the gate.
        const held = getUserAccountTypes(permissionSource).map((type) => type.toLowerCase())
        matched = permissionLevel.map((level) => level.toLowerCase()).some((level) => held.includes(level))
      } else matched = true

      return matched
    },
    [permissionSource],
  )

  const hasPermission = useCallback(
    (permissionLevel: string | Array<string>) => {
      if (permissionLevel.length == 0) return true
      if (permissionSource === undefined) return undefined
      const matched = permissionSource?.role?.permissions?.find((permissionName: string) => {
        if (Array.isArray(permissionLevel) && permissionLevel.includes(permissionName)) return true
        else if (permissionLevel === permissionName) return true
      })
      if (!matched) return false
      return true
    },
    [permissionSource],
  )

  const hasPagePermission = useCallback(
    (pageId: string[]) => {
      return hasPagePermissions(pageId, {
        ...pageParams,
        ...pageQueries,
      })
    },
    [pageParams, pageQueries, data],
  )
  return {
    data,
    isLoading,
    error,
    refetch,
    hasPermissionLink,
    hasPermission,
    hasPagePermission,
  }
}
